import { sessionExpiredPath } from '@medcom/shared-ui/utils/oidc.ts';
import type { User } from 'oidc-client-ts';
import type { AuthContextProps } from 'react-oidc-context';
import { aisIdentityOf } from './useAisIdentity';

export const AIS_LAUNCH_PATH = '/ais/launch';
const AIS_SIGNED_OUT_PATH = '/ais/signed-out';
export const AIS_LAUNCH_ID_KEY = 'ais-launch-id';
export const AIS_LINK_SOURCE_KEY = 'ais-link-source';
export const AIS_LINK_PATH_PREFIX = '/ais/anforderung/';
export const AIS_TAB_NAME = 'labcomplete';

export const isAisLinkPage = (location: Location = window.location) => location.pathname.startsWith(AIS_LINK_PATH_PREFIX);

export const isAisSignedOutPage = (location: Location = window.location) => location.pathname === AIS_SIGNED_OUT_PATH;

// An AIS user cannot use the medcom login form; the signed-out page also sends the next launch to a fresh tab
export const sessionEndedPath = (user: User | null | undefined) =>
  aisIdentityOf(user) ? `${AIS_SIGNED_OUT_PATH}?reason=expired` : sessionExpiredPath();

// not `error`: the OIDC helpers strip that name from every URL, so the reason would be lost in the login
export const aisLaunchError = (location: Location = window.location): 'source' | 'expired' | null => {
  const error = location.pathname === AIS_LAUNCH_PATH ? new URLSearchParams(location.search).get('ais_error') : null;
  if (error === null) {
    return null;
  }
  return error === 'source' ? 'source' : 'expired';
};

export const isAisSessionExpired = (location: Location = window.location) =>
  new URLSearchParams(location.search).get('reason') === 'expired';

// An AIS user cannot use the medcom login form, and a re-login through the still open AIS session would undo the logout
export const signOut = (auth: Pick<AuthContextProps, 'user' | 'signoutRedirect'>) => {
  sessionStorage.removeItem(AIS_LAUNCH_ID_KEY);
  return aisIdentityOf(auth.user)
    ? auth.signoutRedirect({ post_logout_redirect_uri: `${window.location.origin}${AIS_SIGNED_OUT_PATH}` })
    : auth.signoutRedirect();
};

// A launch id of an earlier AIS session must not ride along on a normal create
export const currentAisLaunchId = (user: User | null | undefined): string | undefined =>
  aisIdentityOf(user) ? (sessionStorage.getItem(AIS_LAUNCH_ID_KEY) ?? undefined) : undefined;
